import axios from "axios";
import { useSelector } from "react-redux";
import Strings from "../../resources/Strings";
import { SERVER_ERROR_CODES } from "../../enums/Enums";
import { SERVER_BASE_URL, TIMEOUTS } from "../../config/Config";
import { AuthenticationEntity } from "../../entities/ServicesEntities/LoginEntities/AuthenticationEntity";
import { LOCAL_STORAGE_ITEMS_KEYS } from "../../enums/Enums";
import * as actionsService from "../../services/ActionsService/ActionsService";
import { trackException } from "../../services/Azure/ApplicationInsightsActions";
import * as encryptedLocalStorageActions from "../Storage/EncryptedLocalStorageActions";
import store from "../../redux/Store";
import { userActions } from "../../redux/reducers/UserReducer";
import { commonActions } from "../../redux/reducers/CommonReducer";
import { showNetworkErrorMessage } from "../../utils/MessagesUtils";

const AxiosInstance = axios.create({
  baseURL: SERVER_BASE_URL,
});

AxiosInstance.defaults.timeout = TIMEOUTS.REQUEST_TIMEOUT;
AxiosInstance.defaults.timeoutErrorMessage = Strings.MODAL_ERROR_MESSAGES.NETWORK_TIMEOUT;

AxiosInstance.interceptors.request.use(
  (config) => {
      return new Promise((resolve, reject) => {
        encryptedLocalStorageActions
          .getObjectStorageItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION)
          .then((authentication) => {
            var gmtServerAuthentication = authentication;
  
            if (
              gmtServerAuthentication !== null &&
              gmtServerAuthentication.accessToken !== null
            ) {
              config.headers["Authorization"] =
                "bearer " + gmtServerAuthentication.accessToken;
            }
  
            resolve(config);
          })
          .catch((error) => {
            console.log(
              "AxiosInstance.js - interceptors.request() - Error on getStringStorageItem():",
              error
            );
            trackException(
              "AxiosInstance.js - interceptors.request() - Error on getStringStorageItem():",
              error
            );
            reject(error);
          });
      });
  },
  (error) => {
    console.log("AxiosInstance.js - interceptors.request():", error);
    return Promise.reject(error);
  }
);

AxiosInstance.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  (error) => {
    return new Promise((resolve, reject) => {
      if (error?.code === SERVER_ERROR_CODES.ERR_NETWORK) {
        showNetworkErrorMessage(Strings.MODAL_ERROR_MESSAGES.NETWORK_IS_DOWN);
      } else if (error?.code === SERVER_ERROR_CODES.ECONNABORTED || error?.message?.includes("timeout")) {
        showNetworkErrorMessage(Strings.MODAL_ERROR_MESSAGES.NETWORK_TIMEOUT);
      } else {
        showNetworkErrorMessage(error?.message || "An error occurred");
      }

      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
      console.log("AxiosInstance.js - interceptors.response():", error);
      if (error?.response?.status !== 401) {
        reject(error);
      } else {
        // 401
        console.log("401");
        encryptedLocalStorageActions
          .getObjectStorageItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION)
          .then((authentication) => {
            var gmtServerAuthentication = authentication;
            var user = useSelector((state) => state.user);

            if (gmtServerAuthentication !== null && user !== null) {
              actionsService
                .authenticate(
                  user.userIdentityId,
                  gmtServerAuthentication.refreshToken
                )
                .then((response) => {
                  if (response.data.isSuccess) {
                    var authenticationEntity = new AuthenticationEntity(
                      response.data.entity.accessToken,
                      response.data.entity.refreshToken
                    );
                    encryptedLocalStorageActions
                      .setStorageItem(
                        LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION,
                        authenticationEntity
                      )
                      .then((response) => {
                        const config = error.config;
                        config.headers["Authorization"] =
                          "bearer " + authenticationEntity.accessToken;
                        AxiosInstance.request(config)
                          .then((response) => {
                            resolve(response);
                          })
                          .catch((error) => {
                            console.log(
                              "AxiosInstance.js - interceptors.response() - Error after making new request:",
                              error
                            );
                            trackException(
                              "AxiosInstance.js - interceptors.response() - Error after making new request:",
                              error
                            );
                            reject(error);
                          });
                      })
                      .catch((error) => {
                        console.log(
                          "AxiosInstance.js - interceptors.response() - Error on setStorageItem()",
                          error
                        );
                        trackException(
                          "AxiosInstance.js - interceptors.response() - Error on setStorageItem()",
                          error
                        );
                        reject(error);
                      });
                  } else {
                    console.log(
                      "AxiosInstance.js - interceptors.response() - Error on authenticate()",
                      response.data.error
                    );
                    trackException(
                      "AxiosInstance.js - interceptors.response() - Error on authenticate()",
                      response.data.error
                    );
                    store.dispatch(userActions.logout());
                    reject(error);
                  }
                })
                .catch((error) => {
                  console.log(
                    "AxiosInstance.js - interceptors.response() - Error on authenticate()",
                    error
                  );
                  trackException(
                    "AxiosInstance.js - interceptors.response() - Error on authenticate()",
                    error
                  );
                  store.dispatch(userActions.logout());
                  reject(error);
                });
            } else {
              console.log(
                "AxiosInstance.js - interceptors.response() - gmtServerAuthentication entity or user entity is null"
              );
              trackException(
                "AxiosInstance.js - interceptors.response() - gmtServerAuthentication entity or user entity are null"
              );
              store.dispatch(userActions.logout());
              reject(error);
            }
          })
          .catch((error) => {
            console.log(
              "AxiosInstance.js - interceptors.response() - Error on getStringStorageItem():",
              error
            );
            trackException(
              "AxiosInstance.js - interceptors.response() - Error on getStringStorageItem():",
              error
            );
            store.dispatch(userActions.logout());
            reject(error);
          });
      }
    });
  }
);

export default AxiosInstance;
